import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { SHOW_PUBLIC_PRICING } from '../config/pricing-visibility'
import { TRADES, tradeName } from '../data/trades'
import { T, useLocale, useT } from '../i18n'
import { type FavLane, flagFor, useBox, useFavs, useRecent } from '../lib/personal'
import {
  type IndexRow,
  type IndexTrend,
  type LaneReliability,
  type LaneRow,
  cleanPort,
  fmtPrice,
  getIndexWeeklyAll,
  getLaneReliability,
  laneSlug,
} from '../lib/rates'
import { Sparkline } from './Sparkline'
import { StarButton } from './StarButton'

const boxPrice = (l: LaneRow, box: string) =>
  box === '20gp' ? l.price_20gp : box === '40gp' ? l.price_40gp : l.price_40hc
const YOUR_LANES = 6

interface ResolvedLane {
  pol: string
  pod: string
  polName: string
  podName: string
  price: number | null
  fav: FavLane
}

/** The personal rate terminal — real index, movers, reliability, and the visitor's own lanes. */
export function Dashboard({
  lanes,
  index,
  trend,
}: { lanes: LaneRow[]; index: IndexRow[]; trend: Map<number, IndexTrend> }) {
  const t = useT()
  const locale = useLocale()
  const box = useBox()
  const favs = useFavs()
  const recent = useRecent()
  const [weekly, setWeekly] = useState<Map<number, number[]>>(new Map())
  const [overall, setOverall] = useState<number[]>([])

  useEffect(() => {
    getIndexWeeklyAll()
      .then((rows) => {
        const per = new Map<number, number[]>()
        const byWeek = new Map<string, number[]>()
        for (const r of rows) {
          if (r.median_40hc == null) continue
          const a = per.get(r.line_category_id) ?? []
          a.push(r.median_40hc)
          per.set(r.line_category_id, a)
          const w = byWeek.get(r.week) ?? []
          w.push(r.median_40hc)
          byWeek.set(r.week, w)
        }
        for (const [k, v] of per) per.set(k, v.slice(-8))
        setWeekly(per)
        setOverall(
          [...byWeek.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([, v]) => v.reduce((s, x) => s + x, 0) / v.length)
            .slice(-10)
        )
      })
      .catch((e) => console.error('[frm] index weekly failed', e))
  }, [])

  // Trade rows with a live median — the movers list + headline basket.
  const tradeRows = useMemo(
    () =>
      TRADES.map((tr) => ({ tr, row: index.find((r) => r.line_category_id === tr.id) })).filter(
        (x): x is { tr: (typeof TRADES)[number]; row: IndexRow } => Boolean(x.row?.median_40hc)
      ),
    [index]
  )
  const { big, movePct } = useMemo(() => {
    let vnum = 0
    let vden = 0
    let psum = 0
    let wsum = 0
    for (const { tr, row } of tradeRows) {
      const w = row.lane_count || 1
      vnum += (row.median_40hc ?? 0) * w
      vden += w
      const trd = trend.get(tr.id)
      if (trd) {
        psum += trd.pct_change * w
        wsum += w
      }
    }
    return { big: vden ? Math.round(vnum / vden) : null, movePct: wsum ? psum / wsum : null }
  }, [tradeRows, trend])
  const movers = useMemo(
    () =>
      [...tradeRows].sort(
        (a, b) =>
          Math.abs(trend.get(b.tr.id)?.pct_change ?? 0) -
          Math.abs(trend.get(a.tr.id)?.pct_change ?? 0)
      ),
    [tradeRows, trend]
  )

  // ── Your lanes: favourites first, popular fills the rest ────────────────────
  const laneByKey = useMemo(() => {
    const m = new Map<string, LaneRow>()
    for (const l of lanes) m.set(laneSlug(l.pol_code, l.pod_code), l)
    return m
  }, [lanes])
  const resolve = (f: FavLane): ResolvedLane => {
    const l = laneByKey.get(laneSlug(f.pol, f.pod))
    return {
      pol: f.pol,
      pod: f.pod,
      polName: cleanPort(l?.pol_name ?? f.polName ?? null, f.pol),
      podName: cleanPort(l?.pod_name ?? f.podName ?? null, f.pod),
      price: l ? boxPrice(l, box) : null,
      fav: { pol: f.pol, pod: f.pod, polName: f.polName, podName: f.podName },
    }
  }
  const favResolved = favs.map(resolve)
  const favKeys = new Set(favResolved.map((r) => laneSlug(r.pol, r.pod)))
  const popularFill = lanes
    .filter((l) => !favKeys.has(laneSlug(l.pol_code, l.pod_code)))
    .slice(0, YOUR_LANES)
    .map((l) =>
      resolve({ pol: l.pol_code, pod: l.pod_code, polName: l.pol_name, podName: l.pod_name })
    )
  const yourLanes = [...favResolved, ...popularFill].slice(0, YOUR_LANES)
  const basket = favResolved.map((r) => r.price).filter((p): p is number => p != null)
  const basketAvg = basket.length
    ? Math.round(basket.reduce((s, x) => s + x, 0) / basket.length)
    : null

  // Index-tile spark: real weekly series when we have ≥2 points, else a 2-point slope implied by the
  // w/w move so it never flatlines while history is still building.
  const idxSeries =
    overall.length >= 2
      ? overall
      : big != null && movePct != null
        ? [Math.round(big / (1 + movePct / 100)), big]
        : overall

  // ── Reliability: the visitor's top favourite; if that lane isn't tracked, fall back to a strong
  // default (Shanghai→Rotterdam) so the tile is never empty. Labelled from the row we actually show.
  const relPol = favs[0]?.pol ?? 'CNSHA'
  const relPod = favs[0]?.pod ?? 'NLRTM'
  const [rel, setRel] = useState<LaneReliability | null>(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let rows = await getLaneReliability(relPol, relPod).catch(() => [] as LaneReliability[])
      if (!rows.length && (relPol !== 'CNSHA' || relPod !== 'NLRTM'))
        rows = await getLaneReliability('CNSHA', 'NLRTM').catch(() => [] as LaneReliability[])
      if (!cancelled) setRel(rows[0] ?? null)
    })()
    return () => {
      cancelled = true
    }
  }, [relPol, relPod])

  // Market pulse — the biggest single trade move this week (real, from the index trend).
  const topMover = movers[0]
  const topTrd = topMover ? trend.get(topMover.tr.id) : null
  // Freshness — the most recent source timestamp across the lane set.
  const fresh = lanes.reduce(
    (m, l) => (l.source_updated_at && l.source_updated_at > m ? l.source_updated_at : m),
    ''
  )
  const freshDate = fresh
    ? new Date(fresh).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null

  return (
    <div className="flex flex-col gap-3.5">
      {/* Market pulse — a live, authoritative one-line read of the week */}
      {movePct != null && topMover && topTrd && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted">
          <span className="live-dot pulse-dot" aria-hidden="true" />
          <span className="mono text-[10px] uppercase tracking-[0.18em] text-signal">
            {t('dash.pulse')}
          </span>
          <span>{t('dash.pulseIndex')}</span>
          <span className={`mono font-semibold ${movePct <= 0 ? 'text-down' : 'text-up'}`}>
            {movePct <= 0 ? '▼' : '▲'}
            {Math.abs(movePct).toFixed(1)}% {t('home.wow')}
          </span>
          <span className="text-faint">·</span>
          <span>
            {tradeName(topMover.tr, locale).short}{' '}
            {topTrd.pct_change <= 0 ? t('dash.leadsFall') : t('dash.leadsRise')}
          </span>
          <span
            className={`mono font-semibold ${topTrd.pct_change <= 0 ? 'text-down' : 'text-up'}`}
          >
            {topTrd.pct_change <= 0 ? '▼' : '▲'}
            {Math.abs(topTrd.pct_change).toFixed(1)}%
          </span>
        </div>
      )}

      <div className="grid gap-3.5 lg:grid-cols-[1.15fr_1.5fr]">
        {/* Index */}
        <Tile>
          <TileHead label={t('home.indexTitle')}>
            {movePct != null && (
              <span
                className={`mono rounded-full px-2 py-0.5 text-[10px] ${
                  movePct <= 0 ? 'bg-down/10 text-down' : 'bg-up/10 text-up'
                }`}
              >
                {movePct <= 0 ? '▼' : '▲'} {Math.abs(movePct).toFixed(1)}% {t('home.wow')}
              </span>
            )}
          </TileHead>
          <div className="mono text-[46px] font-semibold leading-none tracking-tight tnum">
            {fmtPrice(big)}
          </div>
          <div className="mono mt-2 text-[10px] uppercase tracking-widest text-faint">
            {t('dash.composite')}
          </div>
          <div className="mt-4">
            <Sparkline pts={idxSeries} down={(movePct ?? 0) <= 0} id="idx" w={300} h={54} />
          </div>
          <div className="mono mt-auto pt-4 text-[11px] tracking-wide text-faint">
            <b className="text-muted">10 {t('home.statCarriers')}</b> ·{' '}
            <b className="text-muted">1,800+ {t('home.statPorts')}</b> ·{' '}
            {t('home.statRefreshValue')} {t('home.statRefresh')}
          </div>
        </Tile>

        {/* Top movers */}
        <Tile>
          <TileHead label={t('dash.movers')}>
            <span className="mono text-[10px] uppercase tracking-widest text-faint">
              {t('dash.priceWow')}
            </span>
          </TileHead>
          <div>
            {movers.map(({ tr, row }) => {
              const trd = trend.get(tr.id)
              const down = trd ? trd.pct_change <= 0 : true
              const wk = weekly.get(tr.id) ?? []
              const series = wk.length >= 2 ? wk : trd ? [trd.earliest_40hc, trd.latest_40hc] : wk
              return (
                <T
                  key={tr.id}
                  to={`/trade/${tr.slug}`}
                  className="group grid grid-cols-[1fr_auto_auto] items-center gap-4 border-t border-border py-3 first:border-t-0 hover:bg-surface-1"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-medium text-ink">
                      {tradeName(tr, locale).short}
                    </div>
                    <div className="mono text-[9.5px] tracking-wide text-faint">
                      {row.lane_count} {t('home.lanesShort')}
                      {row.avg_transit_days ? ` · ~${row.avg_transit_days}d` : ''}
                    </div>
                  </div>
                  <Sparkline pts={series} down={down} id={`m${tr.id}`} w={56} h={22} area={false} />
                  <div className="min-w-[92px] text-right">
                    <span className="mono text-[13.5px] tnum text-ink">
                      {fmtPrice(row.median_40hc)}
                    </span>
                    {trd && (
                      <span className={`mono ml-2 text-[11px] ${down ? 'text-down' : 'text-up'}`}>
                        {down ? '▼' : '▲'}
                        {Math.abs(trd.pct_change).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </T>
              )
            })}
          </div>
        </Tile>

        {/* Reliability */}
        <Tile>
          <TileHead label={t('dash.reliability')}>
            <span className="mono rounded-full bg-up/10 px-2 py-0.5 text-[10px] text-up">
              {t('dash.onTime')}
            </span>
          </TileHead>
          {rel ? (
            <>
              <div className="mono text-[34px] font-semibold leading-none tracking-tight tnum text-up">
                {Math.round(rel.on_time_pct)}%
              </div>
              <div className="mono mt-3 text-[11px] text-muted">
                {rel.carrier} · {rel.observations} {t('dash.sailings')}
              </div>
              <div className="mono mt-1 text-[10px] tracking-wide text-faint">
                {rel.pol_code} → {rel.pod_code}
              </div>
            </>
          ) : (
            <div className="mono py-6 text-[12px] text-faint">{t('dash.relBuilding')}</div>
          )}
        </Tile>

        {/* Your lanes */}
        <Tile>
          <TileHead label={favResolved.length ? t('dash.yourLanes') : t('dash.popular')}>
            {basketAvg != null && (
              <span className="mono text-[11px] tnum text-muted">
                {t('dash.avg')} {fmtPrice(basketAvg)}
              </span>
            )}
          </TileHead>
          {recent.length > 0 && (
            <div className="mb-1.5 flex items-center gap-2 overflow-hidden pb-2">
              <span className="mono shrink-0 text-[9px] uppercase tracking-widest text-faint">
                {t('dash.recent')}
              </span>
              <div className="flex gap-1.5 overflow-x-auto">
                {recent.slice(0, 4).map((r) => (
                  <T
                    key={laneSlug(r.pol, r.pod)}
                    to={`/rates/${laneSlug(r.pol, r.pod)}`}
                    className="mono shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] text-muted hover:border-signal hover:text-ink"
                  >
                    {r.pol}→{r.pod}
                  </T>
                ))}
              </div>
            </div>
          )}
          <div>
            {yourLanes.map((r) => (
              <div
                key={laneSlug(r.pol, r.pod)}
                className="group flex items-center gap-2 border-t border-border py-2 first:border-t-0"
              >
                <StarButton lane={r.fav} />
                <T
                  to={`/rates/${laneSlug(r.pol, r.pod)}`}
                  className="flex min-w-0 flex-1 items-center gap-2 text-[13px] text-ink hover:text-link"
                >
                  <span className="shrink-0">{flagFor(r.pol)}</span>
                  <span className="truncate">{r.polName}</span>
                  <span className="text-faint">→</span>
                  <span className="shrink-0">{flagFor(r.pod)}</span>
                  <span className="truncate">{r.podName}</span>
                </T>
                <span className="mono shrink-0 text-[12.5px] tnum text-muted">
                  {r.price != null ? fmtPrice(r.price) : '—'}
                </span>
              </div>
            ))}
          </div>
          {favResolved.length === 0 && (
            <div className="mono mt-3 text-[11px] text-faint">{t('dash.pinHint')}</div>
          )}
        </Tile>
      </div>

      {/* Proactive CTAs — Pro rate alerts (ties to favourites) + a human freight desk */}
      <div className="grid gap-3.5 sm:grid-cols-2">
        <T
          to="/pricing"
          className="card group flex items-center gap-3.5 p-4 transition-colors hover:border-signal/50"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-signal-soft text-signal">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" />
            </svg>
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-ink">{t('dash.proTitle')}</div>
            <div className="text-[12px] text-muted">
              {favs.length ? t('dash.proBodyFav', { n: favs.length }) : t('dash.proBody')}
            </div>
          </div>
          <span className="mono ml-auto shrink-0 rounded-lg bg-signal px-3 py-2 text-[12px] font-semibold text-on-accent">
            {t('dash.proCta')}
            {SHOW_PUBLIC_PRICING && (
              <>
                {' · $49'}
                <span className="opacity-70">/mo</span>
              </>
            )}
          </span>
        </T>
        <T
          to="/book"
          className="card group flex items-center gap-3.5 p-4 transition-colors hover:border-signal/50"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-1 text-ink">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.4 8.5 8.5 0 0 1-3.9-.9L3 20l1.3-3.8A8.3 8.3 0 0 1 3.6 11.5 8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5Z" />
            </svg>
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-ink">{t('dash.deskTitle')}</div>
            <div className="text-[12px] text-muted">{t('dash.deskBody')}</div>
          </div>
          <span className="mono ml-auto shrink-0 text-[13px] font-semibold text-link group-hover:underline">
            {t('dash.deskCta')} →
          </span>
        </T>
      </div>

      {/* Trust / freshness + the index positioned as a public benchmark */}
      <div className="mono flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-border pt-3 text-[11px] text-faint">
        <span>
          {t('dash.trustLive')} <b className="text-muted">10</b> {t('home.statCarriers')} ·{' '}
          <b className="text-muted">1,800+</b> {t('home.statPorts')} ·{' '}
          <b className="text-muted">{lanes.length}</b> {t('home.lanesShort')}
          {freshDate ? ` · ${t('dash.updated')} ${freshDate}` : ''}
        </span>
        <span>{t('dash.benchmark')}</span>
      </div>
    </div>
  )
}

function Tile({ children }: { children: ReactNode }) {
  return <div className="card flex flex-col p-5">{children}</div>
}
function TileHead({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <span className="mono text-[10px] uppercase tracking-widest text-faint">{label}</span>
      {children}
    </div>
  )
}
